import { MutableRefObject, memo, useMemo } from 'react';
import { VirtualItem } from '@tanstack/react-virtual';

import { DbIdsPerModel } from '../common/ForgeViewer';
import { ViewerMode } from '../common/viewer-mode';
import {
  VirtualFolderNode,
  isVirtualFolderNode,
  HierarchyNode,
  findNodeByPath,
  collectAllShapesInHierarchy,
} from './folder-hierarchy-utils';
import ShapeItem, { ShapeNodeType } from './ShapeItem';
import { isShapeHidden, isShapeSelected } from './ShapesPanel';
import {
  AutoShapeDeepFragment,
  ShapeDeepFragment,
  ShapeFolderDeepFragment,
  SheetShapeDeepFragment,
} from 'src/gql/graphql';

const SHAPE_ITEM_SIZE = 26;

interface ShapeFolderProps {
  sortedFolders: (AutoShapeDeepFragment | ShapeFolderDeepFragment)[];
  virtualItem: VirtualItem;
  virtualItems: VirtualItem[];
  shapesIndexedByFolder: Map<
    string,
    (ShapeDeepFragment | SheetShapeDeepFragment)[]
  >;
  hiddenBIMElements: DbIdsPerModel;
  hiddenSheetElements: DbIdsPerModel;
  expandedPaths: Set<string>;
  selectedFolder: string | null;
  toggleFolderExpansion: (folderId: string) => void;
  selectedShapes: (ShapeDeepFragment | SheetShapeDeepFragment)[];
  handleNodeSelect: (node: ShapeNodeType) => void;
  handleShapeMove: (
    draggedNodeIds: (ShapeDeepFragment | SheetShapeDeepFragment)[],
    targetNode: ShapeFolderDeepFragment | null
  ) => void;
  handleRename: (node: ShapeNodeType, newName: string) => void;
  handleShow: (node: ShapeNodeType) => void;
  handleHide: (node: ShapeNodeType) => void;
  onDeleteHandler: (node: ShapeNodeType) => void;
  setPageNumber: (pageNumber: number) => void;
  changeActiveSheet: (sheetId: string) => void;
  edit3dShape: (shapeToEdit: string) => void;
  editSheetShape: (shapeToEdit: string) => void;
  setViewerMode: (mode: ViewerMode) => void;
  ungroupShapesFromFolder: (node: ShapeNodeType) => Promise<void>;
  deleteFolder: (id: string) => Promise<void>;
  groupShapesIntoFolder: () => Promise<void>;
  selectedShapesRef: MutableRefObject<
    (ShapeDeepFragment | SheetShapeDeepFragment)[]
  >;
  selectedDbIds: Record<string, number[]>;
  isDiffing?: boolean;
  getShapeDiff: (shape: ShapeDeepFragment | SheetShapeDeepFragment) => any;
  getFolderDiff: (
    folder: ShapeFolderDeepFragment | AutoShapeDeepFragment
  ) => any;
  /** Callback when a folder is moved into another folder (renames the folder with new path) */
  onMoveFolder: (folderId: string, newPath: string) => Promise<void>;
  /** Callback when an autoshape is moved into another folder (renames the autoshape with new path) */
  onMoveAutoShape: (autoShapeId: string, newPath: string) => Promise<void>;
  // New props for hierarchy support
  itemsToVirtualize: Array<{
    id: string;
    node:
      | ShapeDeepFragment
      | SheetShapeDeepFragment
      | ShapeFolderDeepFragment
      | AutoShapeDeepFragment
      | VirtualFolderNode;
    level: number;
    displayName: string;
    isVirtual: boolean;
    hasChildren?: boolean;
  }>;
  level: number;
  displayName?: string;
  folderHierarchy: HierarchyNode[];
  /** Folder ID that should auto-start in rename mode */
  renamingFolderId?: string | null;
  /** Callback when rename mode completes */
  onRenameComplete?: () => void;
  /** Whether grouping is allowed for current selection */
  canGroupSelectedShapes?: boolean;
  /** Callback to ungroup a folder (move contents up one level, delete folder) */
  ungroupFolder?: (folderId: string) => Promise<void>;
}

// eslint-disable-next-line complexity
function ShapeFolder({
  sortedFolders,
  virtualItem,
  shapesIndexedByFolder,
  virtualItems,
  hiddenBIMElements,
  hiddenSheetElements,
  expandedPaths,
  selectedFolder,
  selectedShapes,
  toggleFolderExpansion,
  handleRename,
  handleHide,
  handleShow,
  handleShapeMove,
  handleNodeSelect,
  onDeleteHandler,
  changeActiveSheet,
  edit3dShape,
  editSheetShape,
  setPageNumber,
  setViewerMode,
  ungroupShapesFromFolder,
  deleteFolder,
  groupShapesIntoFolder,
  selectedShapesRef,
  selectedDbIds,
  isDiffing,
  getShapeDiff,
  getFolderDiff,
  onMoveFolder,
  onMoveAutoShape,
  itemsToVirtualize,
  level,
  displayName,
  folderHierarchy,
  renamingFolderId,
  onRenameComplete,
  canGroupSelectedShapes,
  ungroupFolder,
}: ShapeFolderProps) {
  const hiddenElements = useMemo(() => {
    return { ...hiddenBIMElements, ...hiddenSheetElements };
  }, [hiddenBIMElements, hiddenSheetElements]);

  // Find the folder/virtual folder from itemsToVirtualize
  const itemInfo = useMemo(
    () => itemsToVirtualize.find((i) => i.id === virtualItem.key),
    [itemsToVirtualize, virtualItem.key]
  );

  // Fallback: look in sortedFolders if not found in itemsToVirtualize
  const folderFromSortedFolders = useMemo(
    () => sortedFolders.find((f) => f.id === virtualItem.key),
    [sortedFolders, virtualItem.key]
  );

  // Get the folder node (could be VirtualFolderNode, ShapeFolderDeepFragment, or AutoShapeDeepFragment)
  // Use itemInfo first, fallback to sortedFolders for compatibility
  const folderNode = itemInfo?.node ?? folderFromSortedFolders;

  // Determine if this is a virtual folder
  const isVirtual = itemInfo?.isVirtual ?? false;

  // Get the path for expansion checking
  const folderPath = useMemo(() => {
    if (!folderNode) return '';
    if (isVirtualFolderNode(folderNode)) {
      return folderNode.id.replace('virtual:', '');
    }
    return folderNode.name;
  }, [folderNode]);

  // Check if expanded using path
  const isExpanded = expandedPaths.has(folderPath);

  // Get shapes for this folder (only for real folders, not virtual ones)
  const rawFolderShapes = useMemo(() => {
    if (!folderNode || isVirtualFolderNode(folderNode)) {
      return [];
    }
    return shapesIndexedByFolder.get(folderNode.id) || [];
  }, [shapesIndexedByFolder, folderNode]);

  // For virtual folders, get all shapes recursively from the hierarchy
  const allShapesInHierarchy = useMemo(() => {
    if (!folderPath) return [];
    const hierarchyNode = findNodeByPath(folderHierarchy, folderPath);
    if (hierarchyNode) {
      return collectAllShapesInHierarchy(hierarchyNode, shapesIndexedByFolder);
    }
    return rawFolderShapes;
  }, [folderPath, folderHierarchy, shapesIndexedByFolder, rawFolderShapes]);

  // Get the folderShapes indexed by the virtualizer (only direct children, not nested)
  const folderShapes = useMemo(
    () =>
      rawFolderShapes.filter((rShape) =>
        virtualItems.find((vItem) => vItem.key === rShape.id)
      ),
    [rawFolderShapes, virtualItems]
  );

  const allChildrenHidden = useMemo(() => {
    const shapesToCheck = isVirtual ? allShapesInHierarchy : rawFolderShapes;
    return (
      shapesToCheck.length > 0 &&
      shapesToCheck.every((shape) =>
        Object.keys(hiddenElements).some(
          (urn) => urn === shape.urn && hiddenElements[urn].includes(shape.dbId)
        )
      )
    );
  }, [isVirtual, allShapesInHierarchy, rawFolderShapes, hiddenElements]);

  // For hierarchical display, we need to check if there are child folders in the hierarchy
  const hasChildFolders = useMemo(() => {
    const hierarchyNode = findNodeByPath(folderHierarchy, folderPath);
    return hierarchyNode ? hierarchyNode.children.length > 0 : false;
  }, [folderHierarchy, folderPath]);

  const hasChildren =
    rawFolderShapes.length > 0 ||
    hasChildFolders ||
    (itemInfo?.hasChildren ?? false);

  const shapeChildren = useMemo(() => {
    const children = folderShapes.map((childShape) => {
      const shapeVirtualItem = virtualItems.find(
        (vi) => vi.key === childShape.id
      );
      const childItemInfo = itemsToVirtualize.find(
        (i) => i.id === childShape.id
      );
      const childLevel = level + 1;
      return {
        key: childShape.id,
        node: childShape,
        isSelected: isShapeSelected(childShape, selectedDbIds),
        isHidden: isShapeHidden(
          childShape,
          hiddenBIMElements,
          hiddenSheetElements
        ),
        onShapeSelect: handleNodeSelect,
        onMoveNode: handleShapeMove,
        onRename: handleRename,
        onShow: handleShow,
        onHide: handleHide,
        onDelete: onDeleteHandler,
        changeActiveSheet,
        editBIMShape: edit3dShape,
        setPageNumber,
        editSheetShape,
        setViewerMode,
        removeShapesFromFolder: ungroupShapesFromFolder,
        deleteFolder,
        groupShapesIntoFolder,
        canGroupSelectedShapes,
        selectedShapesRef,
        top: shapeVirtualItem!.start - virtualItem.start - SHAPE_ITEM_SIZE,
        height: shapeVirtualItem!.size,
        isDiffing,
        diff: getShapeDiff(childShape),
        level: childLevel,
        displayName: childShape.name,
      };
    });
    return children;
  }, [
    folderShapes,
    virtualItems,
    selectedDbIds,
    hiddenBIMElements,
    hiddenSheetElements,
    handleNodeSelect,
    handleShapeMove,
    handleRename,
    handleShow,
    handleHide,
    onDeleteHandler,
    changeActiveSheet,
    edit3dShape,
    setPageNumber,
    editSheetShape,
    setViewerMode,
    ungroupShapesFromFolder,
    deleteFolder,
    groupShapesIntoFolder,
    canGroupSelectedShapes,
    isDiffing,
    getShapeDiff,
    itemsToVirtualize,
    level,
    virtualItem.start,
    selectedShapesRef,
  ]);

  if (!folderNode) return null;

  // Calculate allChildrenSelected using the recursive shapes for virtual folders
  const shapesToCheckForSelection = isVirtual
    ? allShapesInHierarchy
    : rawFolderShapes;
  const allChildrenSelected =
    shapesToCheckForSelection.length > 0 &&
    shapesToCheckForSelection.every((shape) =>
      selectedShapes.some((s) => s.id === shape.id && s.urn === shape.urn)
    );

  // For virtual folders, we can't be "selected" in the traditional sense
  const isSelected = isVirtual
    ? false
    : selectedFolder ===
      (folderNode as ShapeFolderDeepFragment | AutoShapeDeepFragment).id;

  return (
    <ShapeItem
      node={folderNode}
      expanded={isExpanded}
      setExpanded={toggleFolderExpansion}
      isSelected={isSelected}
      allChildrenSelected={allChildrenSelected}
      allChildrenHidden={allChildrenHidden}
      onShapeSelect={handleNodeSelect}
      onMoveNode={handleShapeMove}
      onMoveFolder={onMoveFolder}
      onMoveAutoShape={onMoveAutoShape}
      onRename={handleRename}
      onShow={handleShow}
      onHide={handleHide}
      onDelete={onDeleteHandler}
      isHidden={isShapeHidden(
        folderNode,
        hiddenBIMElements,
        hiddenBIMElements,
        allChildrenHidden
      )}
      changeActiveSheet={changeActiveSheet}
      editBIMShape={edit3dShape}
      setPageNumber={setPageNumber}
      editSheetShape={editSheetShape}
      setViewerMode={setViewerMode}
      removeShapesFromFolder={ungroupShapesFromFolder}
      deleteFolder={deleteFolder}
      groupShapesIntoFolder={groupShapesIntoFolder}
      selectedShapesRef={selectedShapesRef}
      hasChildren={hasChildren}
      top={virtualItem.start}
      height={virtualItem.size}
      shapeChildren={shapeChildren}
      isDiffing={isDiffing}
      diff={
        isVirtual
          ? undefined
          : getFolderDiff(
              folderNode as ShapeFolderDeepFragment | AutoShapeDeepFragment
            )
      }
      level={level}
      displayName={displayName ?? itemInfo?.displayName}
      autoStartRename={renamingFolderId === folderNode?.id}
      onRenameComplete={onRenameComplete}
      canGroupSelectedShapes={canGroupSelectedShapes}
      ungroupFolder={ungroupFolder}
    />
  );
}

export default memo(ShapeFolder);
